/**
 * @file routes/_app/settings/notification-templates.tsx
 * @description 通知模板管理頁面（Task 72）。
 *
 * 功能：
 * - 列表查詢（code / name / inAppTitle / isActive）
 * - 新增/編輯通知模板（全部欄位 + vars JSON textarea）
 * - 硬刪除（notificationTemplates 表無 deletedAt）
 *
 * 視覺：ListPageShell + FilterBar + StatusPill
 */

import { createFileRoute } from '@tanstack/react-router';
import { Button, Col, Form, Input, Modal, Row, Space, Switch, Table, Tooltip } from 'antd';
import { message } from '@/lib/antd-app';
import type { ColumnsType } from 'antd/es/table';
import { Plus, Search, Edit3, Bell, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { ListPageShell, FilterBar, StatusPill } from '@/components/shell';
import { color, font, text, weight } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/settings/notification-templates')({
  component: NotificationTemplatesPage,
});

/**
 * 通知模板實體類型（對應後端 notificationTemplates 表）。
 */
interface NotificationTemplate {
  id: number;
  code: string;
  name: string;
  inAppTitle: string;
  inAppBody: string;
  emailSubject: string | null;
  emailBody: string | null;
  vars: unknown;
  isActive: boolean;
}

/** 後端分頁回應結構 */
interface TemplateListResponse {
  items: NotificationTemplate[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/** 通知模板管理頁面 */
function NotificationTemplatesPage() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm<Record<string, unknown>>();

  /** 查詢模板列表 */
  const { data, isLoading } = useQuery({
    queryKey: ['settings-notification-templates', searchText],
    queryFn: () => {
      const params = new URLSearchParams();
      if (searchText) params.set('search', searchText);
      return apiClient.get<TemplateListResponse>(
        `/api/settings/notification-templates?${params.toString()}`,
      );
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const activeCount = items.filter((t) => t.isActive).length;

  /** 開啟新增表單 */
  const handleAdd = () => {
    setEditingTemplate(null);
    form.resetFields();
    setModalOpen(true);
  };

  /** 開啟編輯表單 */
  const handleEdit = (record: NotificationTemplate) => {
    setEditingTemplate(record);
    form.setFieldsValue({
      ...record,
      varsJson: record.vars ? JSON.stringify(record.vars, null, 2) : '',
    });
    setModalOpen(true);
  };

  /** 表格欄位 */
  const columns: ColumnsType<NotificationTemplate> = [
    {
      title: '代碼',
      dataIndex: 'code',
      width: 200,
      render: (v: string) => (
        <span
          style={{
            fontFamily: font.mono,
            fontSize: text.sm,
            color: color.brand.primary,
            fontWeight: weight.medium,
          }}
        >
          {v}
        </span>
      ),
    },
    {
      title: '名稱',
      dataIndex: 'name',
      width: 150,
      render: (v: string) => (
        <span style={{ fontWeight: weight.medium, color: color.text.primary }}>{v}</span>
      ),
    },
    {
      title: '站內通知標題',
      dataIndex: 'inAppTitle',
      ellipsis: true,
      render: (v: string) => <span style={{ color: color.text.secondary }}>{v}</span>,
    },
    {
      title: '狀態',
      dataIndex: 'isActive',
      width: 80,
      render: (v: boolean) => (
        <StatusPill tone={v ? 'success' : 'muted'} size="sm" dot>
          {v ? '啟用' : '停用'}
        </StatusPill>
      ),
    },
    {
      title: '操作',
      key: '_actions',
      width: 70,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="編輯">
          <Button
            type="text"
            size="small"
            icon={<Edit3 size={14} />}
            onClick={() => handleEdit(record)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <ListPageShell
        title="通知模板管理"
        description="管理系統各事件的站內通知及 Email 通知模板，支援 {{變數}} 占位符。"
        meta={
          <>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: text.sm,
                color: color.text.secondary,
              }}
            >
              <Bell size={13} />
              <span style={{ color: color.text.muted }}>模板總數</span>
              <span style={{ fontWeight: weight.semibold, color: color.text.primary }}>
                {total} 份
              </span>
            </span>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: text.sm,
                color: color.text.secondary,
              }}
            >
              <span style={{ color: color.text.muted }}>啟用中</span>
              <span style={{ fontWeight: weight.semibold, color: color.text.primary }}>
                {activeCount} 份
              </span>
            </span>
          </>
        }
        actions={
          <Button type="primary" icon={<Plus size={14} />} onClick={handleAdd}>
            新增模板
          </Button>
        }
        filters={
          <FilterBar
            search={
              <Input
                placeholder="搜尋代碼 / 名稱"
                prefix={<Search size={14} color={color.text.muted} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            }
            extra={
              searchText && (
                <Button
                  type="text"
                  size="small"
                  icon={<RefreshCw size={13} />}
                  onClick={() => setSearchText('')}
                >
                  重置
                </Button>
              )
            }
          />
        }
      >
        <Table<NotificationTemplate>
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={isLoading}
          scroll={{ x: 900 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t) => `共 ${t} 筆資料`,
          }}
        />

        {/* 序119：通知作業節點開關（可人工啟停各作業節點的自動通知） */}
        <div style={{ marginTop: 24 }}>
          <NotificationNodesCard />
        </div>
      </ListPageShell>

      {/* 新增/編輯 Modal */}
      <Modal
        title={editingTemplate ? '編輯通知模板' : '新增通知模板'}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
        }}
        footer={null}
        width={960}
        destroyOnHidden
      >
        <NotificationTemplateForm
          item={editingTemplate}
          onClose={() => {
            setModalOpen(false);
            form.resetFields();
            void qc.invalidateQueries({ queryKey: ['settings-notification-templates'] });
          }}
          form={form}
        />
      </Modal>
    </>
  );
}

/**
 * 通知模板新增/編輯表單。
 * vars 使用 JSON textarea；其餘欄位為普通文字輸入。
 */
function NotificationTemplateForm({
  item,
  onClose,
  form,
}: {
  item: NotificationTemplate | null;
  onClose: () => void;
  form: ReturnType<typeof Form.useForm<Record<string, unknown>>>[0];
}) {
  const qc = useQueryClient();

  /** 新增模板 Mutation */
  const create = useMutation({
    mutationFn: (v: unknown) => apiClient.post('/api/settings/notification-templates', v),
    onSuccess: () => {
      message.success('通知模板已建立');
      void qc.invalidateQueries({ queryKey: ['settings-notification-templates'] });
      onClose();
    },
  });

  /** 更新模板 Mutation */
  const update = useMutation({
    mutationFn: (v: unknown) =>
      apiClient.patch(`/api/settings/notification-templates/${item!.id}`, v),
    onSuccess: () => {
      message.success('通知模板已更新');
      void qc.invalidateQueries({ queryKey: ['settings-notification-templates'] });
      onClose();
    },
  });

  /**
   * 表單提交前置處理：解析 vars JSON textarea 字串。
   */
  function handleFinish(values: Record<string, unknown>) {
    const payload: Record<string, unknown> = { ...values };
    if (typeof values.varsJson === 'string' && values.varsJson.trim()) {
      try {
        payload.vars = JSON.parse(values.varsJson);
      } catch {
        payload.vars = values.varsJson;
      }
    }
    delete payload.varsJson;
    if (item) {
      update.mutate(payload);
    } else {
      create.mutate(payload);
    }
  }

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={item ? {} : { isActive: true }}
      onFinish={handleFinish}
      style={{ marginTop: 16 }}
    >
      {/* 模板代碼 + 模板名稱 */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="code" label="模板代碼" rules={[{ required: true, max: 64 }]}>
            <Input placeholder="如 audit-task-assigned" disabled={!!item} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="name" label="模板名稱" rules={[{ required: true, max: 100 }]}>
            <Input />
          </Form.Item>
        </Col>
      </Row>

      {/* 站內通知標題（全寬） */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="inAppTitle" label="站內通知標題" rules={[{ required: true, max: 200 }]}>
            <Input />
          </Form.Item>
        </Col>
      </Row>

      {/* 站內通知內文（全寬 textarea） */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="inAppBody" label="站內通知內文" rules={[{ required: true }]}>
            <Input.TextArea rows={3} placeholder="支援 {{變數}} 占位符" />
          </Form.Item>
        </Col>
      </Row>

      {/* Email 主旨（全寬） */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="emailSubject" label="Email 主旨（可選）">
            <Input maxLength={200} />
          </Form.Item>
        </Col>
      </Row>

      {/* Email 內文（全寬 textarea） */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="emailBody" label="Email 內文（可選）">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Col>
      </Row>

      {/* 占位符 JSON + 啟用狀態 */}
      <Row gutter={16}>
        <Col span={16}>
          <Form.Item name="varsJson" label='占位符列表（JSON 陣列；如 ["name","date"]）'>
            <Input.TextArea rows={2} placeholder='["name", "date"]' />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="isActive" label="啟用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item style={{ marginBottom: 0 }}>
        <Space>
          <Button type="primary" htmlType="submit" loading={create.isPending || update.isPending}>
            {item ? '儲存' : '建立'}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}

// ─── 序119：通知作業節點開關 ───────────────────────────────────────────────────

/** 節點項（GET /api/settings/notification-nodes） */
interface NotificationNode {
  key: string;
  name: string;
  enabled: boolean;
  note: string | null;
  updatedAt: string;
}

/**
 * 通知作業節點開關卡片（序119）：
 * 事件型節點（合約評審/報價/繳費/檢測判定…）與日期型節點（證書到期前 4 月、
 * 實驗室證書/合約書到期前 30 天、土地清冊年提醒、送檢逾期提醒——由 worker 每日掃描）
 * 皆可在此人工啟停。
 */
function NotificationNodesCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ items: NotificationNode[] }>({
    queryKey: ['notification-nodes'],
    queryFn: () => apiClient.get('/api/settings/notification-nodes'),
  });

  const toggle = useMutation({
    mutationFn: (v: { key: string; enabled: boolean }) =>
      apiClient.patch(`/api/settings/notification-nodes/${v.key}`, { enabled: v.enabled }),
    onSuccess: () => {
      message.success('節點已更新');
      void qc.invalidateQueries({ queryKey: ['notification-nodes'] });
    },
    onError: (e: Error) => {
      console.error('[NotificationNodesCard] toggle failed', e);
      message.error(e.message || '更新失敗');
    },
  });

  const cols: ColumnsType<NotificationNode> = [
    { title: '作業節點', dataIndex: 'name', width: 220 },
    { title: '說明', dataIndex: 'note', ellipsis: true, render: (v: string | null) => v ?? '—' },
    {
      title: '啟用',
      dataIndex: 'enabled',
      width: 80,
      render: (v: boolean, r) => (
        <Switch
          checked={v}
          size="small"
          onChange={(checked) => toggle.mutate({ key: r.key, enabled: checked })}
        />
      ),
    },
  ];

  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: 16, border: '1px solid #f0f0f0' }}>
      <div style={{ marginBottom: 12 }}>
        <strong>通知作業節點（序119）</strong>
        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
          各作業節點的自動通知可在此人工啟停；日期型節點由系統每日掃描觸發。
        </div>
      </div>
      <Table<NotificationNode>
        rowKey="key"
        size="small"
        columns={cols}
        dataSource={data?.items ?? []}
        loading={isLoading}
        pagination={false}
      />
    </div>
  );
}
